import { useMemo, useState } from "react";
import BellIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/BellIconPath";
import BellActiveIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/BellActiveIconPath";
import BellStrikethroughIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/BellStrikethroughIconPath";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { useTranslation } from "@coworker/locales";
import {
  useStoreId,
  useUID,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  useNotifications,
  useSetNotificationAsUnread,
  useSetAllNotificationsAsUnread,
} from "../hooks/useNotifications";
import { NotificationItem } from "./NotificationItem";
import { useSseNotifications } from "../hooks/useSseNotifications";
import styles from "./notification.module.css";
import { FixaModalHeader } from "../../shared/wrappers/FixaModal/FixaModalHeader";

export const NotificationsInbox = () => {
  const { t } = useTranslation();
  const storeId = useStoreId();
  const uid = useUID();
  const { data: notifications, error } = useNotifications(storeId, uid);
  const { mutateAsync: mutateNotificationAsUnread } =
    useSetNotificationAsUnread(uid);
  const [modalVisible, setModalVisible] = useState(false);
  const latestNotificationCreatedAt = useMemo(() => {
    if (notifications && notifications.length) {
      return notifications[0]!.createdAt;
    }
    return "";
  }, [notifications]);
  useSseNotifications(storeId, uid, latestNotificationCreatedAt);

  const unreadIdArray: string[] = useMemo(
    () =>
      notifications
        ? notifications.reduce((acc, curr) => {
            if (!curr.read) {
              acc.push(curr.id);
            }
            return acc;
          }, [] as string[])
        : [],
    [notifications]
  );
  const { mutateAsync: mutateAllNotificationsAsUnread } =
    useSetAllNotificationsAsUnread(uid);

  const getBellIcon = () => {
    if (error || !notifications) {
      return BellStrikethroughIconPath;
    }
    if (unreadIdArray.length) {
      return BellActiveIconPath;
    } else {
      return BellIconPath;
    }
  };

  const onMarkAllRead = () => {
    if (unreadIdArray.length) {
      mutateAllNotificationsAsUnread(unreadIdArray);
    }
  };

  const inboxStyles = [styles["notification-inbox"] ?? ""];
  if (unreadIdArray.length) {
    inboxStyles.push(styles["notification-inbox-with-unread"] ?? "");
  }

  return (
    <div className={inboxStyles.join(" ")}>
      <FixaButton
        aria-label={t("notificationsString")}
        data-testid="notification inbox button"
        type="tertiary"
        inverseTheme
        onClick={() => setModalVisible(!modalVisible)}
        iconOnly
        ssrIcon={getBellIcon()}
      />
      <FixaModal
        visible={modalVisible}
        handleCloseBtn={() => {
          setModalVisible(false);
        }}
      >
        <FixaSheets
          header={<FixaModalHeader ariaCloseTxt={t("closeActionString")} />}
          footer={null}
          fullSize={true}
        >
          <FixaModalBody className={styles["notification-inbox-body"] ?? ""}>
            <div className={styles["notification-inbox-header"] ?? ""}>
              <div>
                <span>{t("notificationsString")}</span>
              </div>
              <FixaButton
                aria-label={t("markAllReadWithCountString", {
                  count: unreadIdArray.length,
                })}
                data-testid={"mark all read button"}
                text={t("markAllReadWithCountString", {
                  count: unreadIdArray.length,
                })}
                type="tertiary"
                inverseTheme
                onClick={() => onMarkAllRead()}
              />
            </div>
            {notifications !== undefined &&
              notifications?.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={(notificationId) => {
                    setModalVisible(false);
                    mutateNotificationAsUnread(notificationId);
                  }}
                />
              ))}
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </div>
  );
};
